import { bodyCopyLink, Button, ButtonGroup, Divider, FileUpload, Loading, Pullout, PulloutType, Select } from '@aon/aon-tws-common-ui';
import {
	SelectHierarchy as Hierarchy,
	IHierarchyNodeActions,
	IHierarchySearchActionCreators,
	IHierarchyVm,
} from '@aon/tws-common-ui-hierarchy';
import HtmlParser from 'html-react-parser';
import _ from 'lodash';
import * as React from 'react';
import styled from 'styled-components';
import Constants from '../../../../constants';
import ContentConstants from '../../../../contentConstants';
import {
	AudiencePropertiesUnderValidation,
	DistributionType,
	EmailTemplatePropertiesUnderValidation,
	IAudienceSourceVm,
} from '../../../../models';
import { AccessCodeType } from '../../../../models/AccessCodeType';
import { ActionCreators, AudienceDetailsState } from '../../../../store/AudienceDetailsStore';
import { ActionCreators as CommonHierarchyActionCreators, TCommonHierarchyState } from '../../../../store/CommonHierarchyStore';
import { ActionCreators as DistributionContactActionCreators, IDistributionContactState } from '../../../../store/DistributionContactStore';
import { ActionCreators as DistributionFileActionCreators, IDistributionFileState } from '../../../../store/DistributionFileStore';
import {
	ActionCreators as DistributionHierarchyActionCreators,
	IDistributionHierarchyState,
} from '../../../../store/DistributionHierarchyStore';
import { ActionCreators as EmailActionCreators, IEmailState } from '../../../../store/EmailStore';
import * as EmailTemplateActionCreators from '../../../../store/EmailTemplateSlice';
import { ActionCreators as ExternalHierarchyActionCreators, IExternalHierarchyState } from '../../../../store/ExternalHierarchyStore';
import { ISurveyParticipantState, ActionCreators as SurveyParticipantActionCreators } from '../../../../store/SurveyParticipantStore';
import { SurveyHelper, ValidationErrorHelper } from '../../../../utilities';
import { DistributionHelper } from '../../../../utilities/DistributionHelper';
import { AudienceMode } from '../../../audiences/AudienceMode';
import { CardLayout, ClearDiv, Col3Col9, LabelAbove, LoadingWrapper, SaveButtonsContainer, Section, WarningModal } from '../../../common';
import { AccessCodes } from '../../accessCodes/AccessCodes';
import * as AudienceValidation from '../../AudienceValidation';
import { DistributionNotificationRouter } from '../../distributionOptions/DistributionNotificationRouter';
import { DistributionOptionDataTransferModal } from '../../distributionOptions/DistributionOptionDataTransferModal';
import { DistributionTypeOptions } from '../../distributionOptions/DistributionTypeOptions';
import { FileUploadError } from '../../distributions/FileUploadError';
import { ErrorMessageLabel } from '../../SharedComponents';

export const BlockAfter = styled.div`
	margin-top: 5px;
	font-size: 11px !important;
	span {
		margin-left: 5px;
	}
`;
export const InputLink = styled.a`
	display: block;
	font-size: 12px !important;
	width: fit-content;
	margin-top: 5px;
	${bodyCopyLink};
`;
InputLink.displayName = 'InputLink';

export const TemplateButtonsContainer = styled.div`
	margin-bottom: 30px;
	margin-top: 16px;
	& .btn {
		margin-right: 10px;
	}
	& .tws-button-group {
		align-items: start;
	}
`;
TemplateButtonsContainer.displayName = 'TemplateButtonsContainer';

interface IHierarchyNodeSelectionProps {
	audienceSource?: IAudienceSourceVm;
	mode: AudienceMode;
	surveyId: number;
	surveySupportedCultures?: string[];
}

interface IHierarchyNodeSelectionState {
	validationErrorHelper: ValidationErrorHelper<AudiencePropertiesUnderValidation>;
}

export type HierarchyNodeSelectionParams = IHierarchyNodeSelectionProps &
	typeof ActionCreators &
	AudienceDetailsState &
	typeof EmailActionCreators &
	IEmailState &
	typeof EmailTemplateActionCreators &
	EmailTemplateActionCreators.IEmailTemplateState &
	typeof DistributionFileActionCreators &
	IDistributionFileState &
	typeof DistributionContactActionCreators &
	IDistributionContactState &
	typeof DistributionHierarchyActionCreators &
	IDistributionHierarchyState &
	typeof SurveyParticipantActionCreators &
	ISurveyParticipantState &
	typeof ExternalHierarchyActionCreators &
	IExternalHierarchyState &
	typeof CommonHierarchyActionCreators & { hierarchy: TCommonHierarchyState } & IHierarchyNodeActions &
	IHierarchySearchActionCreators;

export class HierarchyNodeSelection extends React.Component<HierarchyNodeSelectionParams, IHierarchyNodeSelectionState> {
	constructor(props: any) {
		super(props);

		this.closeUnsavedWarningModal = this.closeUnsavedWarningModal.bind(this);
		this.discardUnsavedChanges = this.discardUnsavedChanges.bind(this);
		this.discardUnsavedChangesAndGotoList = this.discardUnsavedChangesAndGotoList.bind(this);
		this.handleHierarchySelection = this.handleHierarchySelection.bind(this);
		this.goPreviousStep = this.goPreviousStep.bind(this);
		this.validateInvitationEmail = this.validateInvitationEmail.bind(this);
		this.determineSaveMethod = this.determineSaveMethod.bind(this);
	}

	public async componentDidMount() {
		if (this.props.audience.audienceId !== 0) {
			this.props.GetDistributionHierarchy(this.props.surveyId, this.props.audience.audienceId);
		}
		if (this.props.selectedHierarchyId) {
			this.props.GetHierarchyVm(this.props.selectedHierarchyId, false);
		}
		if (this.props.audienceSource?.externalHrisId) {
			this.props.GetHrisHierarchies(this.props.audienceSource?.externalHrisId);
		}
	}

	public componentDidUpdate(prevProps: HierarchyNodeSelectionParams) {
		const { surveyId, selectedHierarchyId, audienceSource, audience } = this.props;
		if (audience.audienceId !== 0 && audience.audienceId !== prevProps.audience.audienceId) {
			this.props.GetDistributionHierarchy(surveyId, audience.audienceId);
		}
		if (selectedHierarchyId && selectedHierarchyId !== prevProps.selectedHierarchyId) {
			this.props.GetHierarchyVm(selectedHierarchyId, false);
		}
		if (audienceSource?.externalHrisId && audienceSource?.externalHrisId !== prevProps.audienceSource?.externalHrisId) {
			this.props.GetHrisHierarchies(audienceSource?.externalHrisId);
		}
	}

	public componentWillUnmount() {
		this.props.ResetHierarchyState();
	}

	public getValidationHelper(props: HierarchyNodeSelectionParams) {
		let validationErrors = { ...props.audience.validationErrors };
		if (props.distributionHierarchy.distributionHierarchyValidationError) {
			validationErrors = _.merge(validationErrors, props.distributionHierarchy.distributionHierarchyValidationError);
		}

		return new ValidationErrorHelper<AudiencePropertiesUnderValidation>(validationErrors);
	}

	public goPreviousStep() {
		const { audience, distribution, fileUploadType, file, invitationEmail, reminderEmails, mode } = this.props;
		this.props.GoToPreviousStep(audience, mode, invitationEmail, reminderEmails, distribution, fileUploadType, file);
	}

	public validateInvitationEmail = (fieldNames?: EmailTemplatePropertiesUnderValidation[]) => {
		const { invitationEmail, invitationEmailValidationErrors, audience } = this.props;

		let inviteEmailValidationErrors = AudienceValidation.validEmailTemplateFields(
			fieldNames || [
				EmailTemplatePropertiesUnderValidation.From,
				EmailTemplatePropertiesUnderValidation.ReplyToEmail,
				EmailTemplatePropertiesUnderValidation.Subject,
				EmailTemplatePropertiesUnderValidation.Content,
				EmailTemplatePropertiesUnderValidation.SurveyLink,
			],
			invitationEmail,
			audience.distributionType,
		);

		// Remove survey link validation error
		if (
			fieldNames &&
			fieldNames.length === 1 &&
			fieldNames[0] === EmailTemplatePropertiesUnderValidation.SurveyLink &&
			invitationEmailValidationErrors
		) {
			inviteEmailValidationErrors = _.omit(invitationEmailValidationErrors, [
				EmailTemplatePropertiesUnderValidation.Content,
				EmailTemplatePropertiesUnderValidation.SurveyLink,
			]);
		}
		this.props.addInvitationEmailValidationErrors(inviteEmailValidationErrors);
		return inviteEmailValidationErrors;
	};

	public discardUnsavedChangesAndGotoList() {
		const { audience, mode } = this.props;
		this.props.ToggleUnsavedWarningModal(false);
		this.props.GoToAudienceDetails(audience.surveyId, mode, audience.audienceId);
	}

	public discardUnsavedChanges() {
		const { pendingDistributionType } = this.props;
		this.props.ToggleUnsavedWarningModal(false);
		this.props.HandleDistributionDataTransfer(pendingDistributionType!, false);
	}

	public closeUnsavedWarningModal() {
		this.props.ToggleUnsavedWarningModal(false);
	}

	public handleHierarchySelection(value?: string | string[]) {
		if (value !== undefined && !Array.isArray(value)) {
			this.props.SelectHierarchy(value);
		}
	}

	public render() {
		const { audience, distributionOptionDataTransferModalOpen, lastSavedAudience, loading, mode, unsavedChangesWarningModalOpen } =
			this.props;
		return (
			<CardLayout title={ContentConstants.DISTRIBUTIONOPTIONS_PAGE_TITLE} loading={loading}>
				<Section sectionHeader={ContentConstants.SECTIONHEADERS_DISTRIBUTION_OPTIONS}>
					<DistributionTypeOptions />
					{audience.distributionType && <Divider type='dashed' />}

					<DistributionNotificationRouter />
					{audience.distributionType && <Divider type='dashed' />}

					{this.getHierarchySelectionContent(audience.distributionType)}
				</Section>
				<Divider type='dashed' />
				<SaveButtonsContainer style={{ textAlign: 'center' }}>
					<ButtonGroup type='column' reversed={false}>
						<Button id='PreviousStepButton' color='secondary' size='lg' onClick={this.goPreviousStep}>
							Previous Step
						</Button>
						<Button
							id='Save'
							color='secondary'
							disabled={this.props.saveInProgress || this.props.isUploading || this.props.isTransferDataFileValidating}
							onClick={() => this.determineSaveMethod(false, mode)}
							size='lg'
						>
							{ContentConstants.BUTTONS_SAVE}
						</Button>
						<Button
							id='SaveAndCompleteButton'
							color='primary'
							size='lg'
							disabled={this.props.saveInProgress || this.props.isUploading || this.props.isTransferDataFileValidating}
							onClick={() => this.determineSaveMethod(true, mode)}
						>
							{mode === AudienceMode.Edit ? ContentConstants.BUTTONS_SAVE_AND_COMPLETE : ContentConstants.BUTTONS_SAVE_AND_PUBLISH}
						</Button>
					</ButtonGroup>
				</SaveButtonsContainer>
				{unsavedChangesWarningModalOpen && (
					<WarningModal
						isOpen={true}
						contentHeadline={ContentConstants.WARNING_MODAL_UNSAVED_HEADLINE}
						contentBody={ContentConstants.WARNING_MODAL_UNSAVED_BODY}
						onSubmit={this.discardUnsavedChangesAndGotoList}
						onClose={this.closeUnsavedWarningModal}
					/>
				)}

				{distributionOptionDataTransferModalOpen &&
					(!lastSavedAudience.distributionType ||
						(lastSavedAudience.distributionType &&
							this.props.pendingDistributionType === DistributionType.SingleLinkWithoutDistribution)) && (
						<WarningModal
							isOpen={true}
							contentHeadline={ContentConstants.WARNING_DISTRIBUTION_OPTION_WARNING_HEADLINE}
							contentBody={ContentConstants.WARNING_DISTRIBUTION_OPTION_WARNING_BODY}
							onSubmit={this.discardUnsavedChanges}
							onClose={this.closeUnsavedWarningModal}
						/>
					)}

				{distributionOptionDataTransferModalOpen &&
					lastSavedAudience.distributionType &&
					this.props.pendingDistributionType !== DistributionType.SingleLinkWithoutDistribution && (
						<DistributionOptionDataTransferModal
							modalOpen={lastSavedAudience.distributionType && distributionOptionDataTransferModalOpen}
							closeModal={() => this.props.ToggleDistributionOptionDataTransferModal(false)}
							onSubmit={(transfer: boolean) => this.props.HandleDistributionDataTransfer(this.props.pendingDistributionType!, transfer)}
						/>
					)}
			</CardLayout>
		);
	}

	private determineSaveMethod(advance: boolean, mode: AudienceMode) {
		if (
			this.props.audience.distributionType === DistributionType.SingleLinkWithAccessCode &&
			this.props.audience.accessCodeType === AccessCodeType.Custom
		) {
			const selectedDistributionHierarchy = this.getHierarchySelection();
			this.props.validateHierarchyAccessCodeSave(
				this.props.surveyId,
				this.props.audience.audienceId,
				selectedDistributionHierarchy,
				mode,
				advance,
				this.props.selectedHierarchyId,
			);
		} else if (this.props.audience.distributionType === DistributionType.SingleLinkWithoutDistribution) {
			this.props.Save(mode, advance);
		} else {
			this.props.SaveAudienceWithHierarchy(mode, advance);
		}
	}

	private getHierarchySelectionContent(distributionType?: DistributionType) {
		if (!DistributionHelper.usesDistributionList(distributionType)) {
			return <></>;
		}

		const validationHelper = this.getValidationHelper(this.props);
		const { hierarchy, selectedHierarchyId, hierarchyMetadatas } = this.props;

		return (
			<>
				{distributionType === DistributionType.SingleLinkWithAccessCode && (
					<>
						<AccessCodes labelSize='lg' />
						<Col3Col9>{this.CustomAccessCodeFile(this.props.audience.accessCodeType === AccessCodeType.Custom)}</Col3Col9>
					</>
				)}

				<Col3Col9 label='Distribution List' labelSize='lg' clearBefore='40px' className='tws-hierarchy-selection-instructions'>
					{!validationHelper.IsValid(AudiencePropertiesUnderValidation.DistributionType) && (
						<ErrorMessageLabel>{validationHelper.Errors(AudiencePropertiesUnderValidation.DistributionType)}</ErrorMessageLabel>
					)}

					{!validationHelper.IsValid(AudiencePropertiesUnderValidation.Distribution) && (
						<ErrorMessageLabel>{validationHelper.Errors(AudiencePropertiesUnderValidation.Distribution)}</ErrorMessageLabel>
					)}

					<LabelAbove type='input' textIsHtml={true} text={ContentConstants.INSTRUCTIONALTEXT_DISTRIBUTION_HIERARCHY_SELECTION} />

					{/* If emp file dependencies are N/A, display a warning */}
					{[hierarchy, hierarchyMetadatas].some((dep) => !dep.isLoading && dep.couldNotFetchData) && (
						<div>
							<Pullout type={PulloutType.Warning}>{HtmlParser(ContentConstants.EMPLOYEEDATASOURCE_NOTAVAIL_WARNING)}</Pullout>
							<ClearDiv height='10' />
						</div>
					)}
					{this.HierarchySelector()}
					<ClearDiv height={'20'} />
					{selectedHierarchyId &&
						(!hierarchy.isLoading && hierarchy.couldNotFetchData ? undefined : (
							<>
								<LabelAbove type='input' textIsHtml={true} text={this.HierarchyNodeSelectionInstructionText(selectedHierarchyId)} />
								{this.HierarchyNodeSelector(hierarchy)}
							</>
						))}
				</Col3Col9>
			</>
		);
	}

	private HierarchySelector() {
		const { hierarchyMetadatas, selectedHierarchyId } = this.props;

		if (hierarchyMetadatas.isLoading) {
			return (
				<LoadingWrapper>
					<Loading height='35px' width='35px' />
				</LoadingWrapper>
			);
		}

		const selectorBaseProps: React.ComponentProps<typeof Select> = {
			className: 'hierarchy-collection',
			selectSize: 'xlg',
		};

		if (hierarchyMetadatas.couldNotFetchData) {
			return <Select {...selectorBaseProps} displayText={'N/A'} disabled={true} />;
		}

		return (
			<Select
				{...selectorBaseProps}
				displayText='Select Your Hierarchy'
				items={SurveyHelper.getSelectOptionsFromHierarchyAttributes(hierarchyMetadatas)}
				selectedValue={selectedHierarchyId}
				onApply={this.handleHierarchySelection}
			/>
		);
	}

	private HierarchyNodeSelectionInstructionText(selectedHierarchyId: string) {
		return ContentConstants.INSTRUCTIONALTEXT_DISTRIBUTION_NODE_SELECTION.replace(
			'{url}',
			new URL(`hierarchy/${selectedHierarchyId}`, Constants.ORGMANAGER_URL).href,
		);
	}

	private HierarchyNodeSelector(hierarchy: IHierarchyVm) {
		if (!hierarchy.hierarchyHeader) {
			return (
				<LoadingWrapper>
					<Loading height='35px' width='35px' />
				</LoadingWrapper>
			);
		}

		return (
			<Hierarchy
				{...this.props}
				{...hierarchy}
				{...ActionCreators}
				isLoading={false}
				enableDismiss={true}
				selectionCriteria={this.props.selectionCriteria}
				onChangeSelectionCriteria={this.props.UpdateHierarchyNodeSelection}
			/>
		);
	}

	private CustomAccessCodeFile(isCustomAccessCodeEnabled: boolean) {
		if (!isCustomAccessCodeEnabled) {
			return <></>;
		} else {
			const {
				accessCodeUploadValidationErrors,
				distributionHierarchy,
				selectedHierarchyId,
				selectionCriteria,
				surveyId,
				UploadHierarchyAccessCodes,
			} = this.props;
			const { audienceId } = this.props.audience;
			const selectedDistributionHierarchy = this.getHierarchySelection();
			const hasSelectionBeenMade =
				distributionHierarchy &&
				selectedHierarchyId &&
				selectionCriteria &&
				Object.keys(selectionCriteria.descendantsIncluded).length !== 0;
			return (
				<>
					{ContentConstants.INSTRUCTIONALTEXT_DISTRIBUTION_HIERARCHY_CUSTOM_ACCESS_CODES}
					<TemplateButtonsContainer>
						<ButtonGroup type='column' reversed={false}>
							<Button
								id='btnDownloadTemplate'
								size='sm'
								disabled={!hasSelectionBeenMade}
								onClick={() => this.props.GetHierarchyAccessCodeDownload(surveyId, audienceId, selectedDistributionHierarchy)}
							>
								{ContentConstants.BUTTONS_DOWNLOAD_TEMPLATE}
							</Button>
							<FileUpload
								btnSize={'sm'}
								btnTitle={ContentConstants.BUTTONS_UPLOAD_FILE}
								btnColor={'secondary'}
								disabled={!hasSelectionBeenMade}
								multiple={false}
								getValue={(files: FileList) => UploadHierarchyAccessCodes(surveyId, audienceId, files[0])}
								iconName='upload'
								fileNameLabelWidth={'250px'}
								error={accessCodeUploadValidationErrors && Object.keys(accessCodeUploadValidationErrors).length > 0}
							/>
						</ButtonGroup>
					</TemplateButtonsContainer>
					{accessCodeUploadValidationErrors && <FileUploadError distributionValidationErrors={accessCodeUploadValidationErrors} />}
				</>
			);
		}
	}

	private getHierarchySelection() {
		const { distributionHierarchy, selectedHierarchyId, selectionCriteria } = this.props;
		const selectedDistributionHierarchy = { ...distributionHierarchy };
		const hasSelectionBeenMade =
			distributionHierarchy && selectedHierarchyId && selectionCriteria && Object.keys(selectionCriteria.descendantsIncluded).length !== 0;
		if (hasSelectionBeenMade) {
			selectedDistributionHierarchy.hierarchySelection = JSON.stringify(selectionCriteria);
			selectedDistributionHierarchy.externalHierarchyId = selectedHierarchyId;
		}
		return selectedDistributionHierarchy;
	}
}
